/**
 * 用户 Context
 * 提供向后兼容的 Context API 和统一的 hooks
 */
import React, { useEffect } from 'react'
import useUserStore from '../stores/useUserStore'

/**
 * useUser Hook - 主要的用户状态 hook
 * @returns {Object} 用户状态和操作方法
 */
export const useUser = () => {
  const store = useUserStore()
  
  return {
    // 状态
    user: store.user,
    loading: store.isLoading,
    isLoading: store.isLoading,
    error: store.error,
    isLoggedIn: !!store.user?.token,
    clients: store.config?.clients || {},
    config: store.config,
    
    // 操作
    login: store.login,
    logout: store.logout,
    updateUserData: store.updateUserData,
    clearError: store.clearError,
    initialize: store.initialize,
  }
}

/**
 * UserProvider 组件
 * 负责初始化应用状态
 */
export const UserProvider = ({ children }) => {
  const initialize = useUserStore((state) => state.initialize)
  
  useEffect(() => {
    initialize()
  }, [initialize])

  // 监听未授权事件（401）
  useEffect(() => {
    const handleUnauthorized = () => {
      const logout = useUserStore.getState().logout
      logout()
      // 可以在这里添加跳转到登录页的逻辑
      window.location.href = '/login'
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized)
    }
  }, [])

  return <>{children}</>
}

/**
 * useAuth Hook - 认证相关操作
 */
export const useAuth = () => {
  const { login, logout, isLoggedIn, isLoading, error, clearError } = useUser()
  
  return {
    login,
    logout,
    isLoggedIn,
    isLoading,
    error,
    clearError,
  }
}

/**
 * useUserInfo Hook - 用户信息
 */
export const useUserInfo = () => {
  const { user, updateUserData } = useUser()
  
  return {
    user,
    email: user?.email,
    token: user?.token,
    baseUrl: user?.baseUrl,
    updateUserData,
  }
}

export default useUserStore
